import { useQuery } from 'convex/react';
import { api } from '../../api';
import { useUserDevice } from './useUserDevice';

interface AppCategory {
  name: string;
  tags: string[];
}

interface CategoryStatus {
  status: 'blocked' | 'allowed' | 'allowed_with_rules';
  source: 'device' | 'level';
  isAllowed: boolean;
}

const getCategoryStatus = (category: AppCategory, appFilter: any): CategoryStatus => {
  // Check if category exists in appFilter
  const categoryConfig = appFilter?.[category.name];
  
  if (!categoryConfig) {
    // Category not found - blocked
    return {
      status: 'blocked',
      source: 'device',
      isAllowed: false
    };
  }

  const { tagFilter, source = 'device' } = categoryConfig;

  if (tagFilter === false) {
    // Explicitly blocked
    return {
      status: 'blocked',
      source,
      isAllowed: false
    };
  }

  if (tagFilter === true) {
    // Explicitly allowed
    return {
      status: 'allowed',
      source,
      isAllowed: true
    };
  }

  if (typeof tagFilter === 'object' && tagFilter !== null) {
    // Allowed with rules
    return {
      status: 'allowed_with_rules',
      source,
      isAllowed: true
    };
  }

  // Default fallback - blocked
  return {
    status: 'blocked',
    source,
    isAllowed: false
  };
};

export const useAppCategories = () => {
  const { device } = useUserDevice();

  // Fetch app categories from Convex - using list endpoint
  const categories = useQuery(api.appCategories.list.categories);

  // Fetch device details to get deviceConfig
  const deviceDetails = useQuery(
    api.devices.get.details,
    device?._id ? { device: device._id as any } : "skip"
  );

  // Fetch device app configuration
  const deviceAppConfig = useQuery(
    api.devices.apps.getConfig,
    deviceDetails?.device?.deviceConfig ? { deviceConfig: deviceDetails.device.deviceConfig } : "skip"
  );

  // Combine categories with their device-specific status
  const categoriesWithStatus = categories?.map(category => {
    const categoryStatus = getCategoryStatus(category, deviceAppConfig?.appFilter);
    return {
      ...category,
      ...categoryStatus
    };
  }) || [];

  return {
    categories: categoriesWithStatus,
    isLoading: categories === undefined || (device && deviceAppConfig === undefined),
    hasDevice: !!device,
    deviceAppConfig,
    refetch: () => {
      // Convex handles refetching automatically
    },
  };
};